import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react'; // eslint-disable-line import/no-extraneous-dependencies
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import { ENV } from 'utils/config';
import { checkoutPageUrl } from 'utils/urlGenerator';
import accessLocalStorage from 'services/localStorage';
import treezPayHeader from 'assets/img/brand/TreezPay-logo-header.svg';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';

const SDK_COMPONENT = 'sdk';
const SDK_NAMESPACE = 'paySdkFeatureFlag';
const SDK_CHANNEL = 'pos';

const StaticQR = (): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const entityId = accessLocalStorage.getEntityId();
  const qrUrl = `${checkoutPageUrl(ENV)}/?entityId=${entityId}`;

  const {
    toFetchComponentSdkConfig,
    toUpdateComponentSdkConfig,
    paymentConfig,
  } = usePaymentConfig();

  const { componentSdkConfig: allSdkConfigs, componentSdkConfigUpdated, error } = paymentConfig;
  const componentSdkConfig = allSdkConfigs?.[SDK_CHANNEL];

  const [enableStaticQR, setEnableStaticQR] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const justSavedRef = useRef(false);

  useEffect(() => {
    toFetchComponentSdkConfig({
      component: SDK_COMPONENT,
      entityId,
      namespace: SDK_NAMESPACE,
      channel: SDK_CHANNEL,
    });
  }, []);

  useEffect(() => {
    if (componentSdkConfig?.value?.enableStaticQR !== undefined) {
      setEnableStaticQR(componentSdkConfig.value.enableStaticQR);
    }
  }, [componentSdkConfig]);

  useEffect(() => {
    if (componentSdkConfigUpdated && justSavedRef.current) {
      toast.success('Static QR settings saved successfully');
      setIsSaving(false);
      justSavedRef.current = false;
    }
  }, [componentSdkConfigUpdated]);

  useEffect(() => {
    if (error?.updateComponentSdkConfig && justSavedRef.current) {
      toast.error('Something went wrong! Please try again later.');
      setIsSaving(false);
      justSavedRef.current = false;
    }
  }, [error?.updateComponentSdkConfig]);

  const isDirty =
    enableStaticQR !== null &&
    enableStaticQR !== componentSdkConfig?.value?.enableStaticQR;

  const handleSave = () => {
    if (!componentSdkConfig || enableStaticQR === null) return;
    setIsSaving(true);
    justSavedRef.current = true;
    toUpdateComponentSdkConfig({
      component: SDK_COMPONENT,
      entityId,
      namespace: SDK_NAMESPACE,
      channel: SDK_CHANNEL,
      schemaVersion: componentSdkConfig.schemaVersion,
      value: { ...componentSdkConfig.value, enableStaticQR },
    });
  };

  const handleCopy = (_text: string, copied: boolean) => {
    if (copied) {
      toast.success('QR URL copied to clipboard');
    } else {
      toast.error("Couldn't copy the QR URL. Please try again.");
    }
  };

  // Chrome skips CSS backgrounds when printing unless "Background graphics" is
  // ticked, so everything visible in the printout must be an image or a border.
  // No box-shadow either: macOS PDF viewers flatten it into a grey box.
  const handlePrint = () => {
    const canvas = wrapperRef.current?.querySelector('canvas') ?? null;
    if (!canvas) return;

    const qrDataUrl = canvas.toDataURL('image/png');

    const printWin = window.open('', '_blank');
    if (!printWin) return;

    printWin.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>TreezPay QR Code</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 28px 32px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      width: 280px;
    }
    .header { display: block; width: 100%; }
    .divider { width: 100%; border-top: 1px solid #e2e8f0; }
    .qr-wrapper {
      padding: 8px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
    }
    .qr-wrapper img { display: block; width: 180px; height: 180px; }
    .scan-label { font-size: 15px; font-weight: 600; color: #333D4E; letter-spacing: 0.01em; }
    .qr-url { font-size: 10px; color: #7A8599; word-break: break-all; text-align: center; }
  </style>
</head>
<body>
  <div class="card">
    <img class="header" src="${treezPayHeader}" alt="TreezPay" />
    <div class="divider"></div>
    <div class="qr-wrapper">
      <img src="${qrDataUrl}" alt="QR Code" />
    </div>
    <span class="scan-label">Scan to pay</span>
  </div>
</body>
</html>`);
    printWin.document.close();
    printWin.focus();
    printWin.onload = () => {
      printWin.print();
    };
  };

  return (
    <div className="py-8 px-6 flex flex-col items-start gap-5">
      <h2 className="text-base font-semibold text-gray-blue-80 m-0">Static QR Code</h2>
      <p className="text-sm text-gray-blue-70 m-0 max-w-120">
        Display or print this QR code in your store. Customers can scan it to
        go directly to your payment page.
      </p>

      <div className="flex flex-col gap-4 w-120 px-5 py-4 border border-gray-blue-10 rounded-lg bg-white">
        <div className="flex justify-between items-start mb-1">
          <div>
            <Label
              htmlFor="enableStaticQR"
              className="text-sm font-semibold text-gray-blue-80 mb-1"
            >
              Enable Static QR
            </Label>
            <p className="text-sm text-gray-blue-70 m-0">
              Enable static QR payment feature
            </p>
          </div>
          <Switch
            id="enableStaticQR"
            checked={enableStaticQR ?? false}
            disabled={enableStaticQR === null}
            onCheckedChange={setEnableStaticQR}
            className="mt-1"
          />
        </div>
        <Button disabled={!isDirty || isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {entityId ? (
        <>
          <div className="flex flex-col items-center gap-4 py-7 px-8 border border-gray-blue-10 rounded-xl bg-white shadow-md w-70">
            <img src={treezPayHeader} alt="TreezPay" className="w-full" />
            <div className="w-full h-px bg-gray-blue-10" />
            <div className="p-2 border border-gray-blue-10 rounded-lg" ref={wrapperRef}>
              <QRCodeCanvas value={qrUrl} size={180} level="H" />
            </div>
            <p className="text-sm font-semibold text-gray-blue-80 m-0">Scan to pay</p>
          </div>

          <Button
            iconStart="printer"
            iconClassName="text-dark-green-text"
            className="w-70 justify-center"
            onClick={handlePrint}
          >
            Print QR Code
          </Button>

          <CopyToClipboard text={qrUrl} onCopy={handleCopy}>
            <Button
              iconStart="copy"
              iconClassName="text-dark-green-text"
              className="w-70 justify-center"
            >
              Copy QR URL
            </Button>
          </CopyToClipboard>
        </>
      ) : (
        <p className="text-sm text-gray-blue-70 m-0 max-w-120">
          No store configured. QR code unavailable.
        </p>
      )}

    </div>
  );
};

export default StaticQR;
